import {
  FINANCIAL_SCHEMA_VERSION,
  TRANSACTION_STATUS,
  TRANSACTION_TYPES,
  isValidStatusForType,
} from './constants';
import { getFinancialDate } from './dates';
import { getAmountCents, centsToLegacyValue } from './money';

export const FIRESTORE_FINANCIAL_SCHEMA_VERSION = 3;

export const FIRESTORE_TRANSACTION_TYPES = Object.freeze({
  INCOME: 'income',
  EXPENSE: 'expense',
});

export const FIRESTORE_TRANSACTION_STATUS = Object.freeze({
  RECEIVED: 'received',
  PAID: 'paid',
  PENDING: 'pending',
});

export const toFirestoreYearMonth = (period) => {
  const match = typeof period === 'string' && period.match(/^(\d{1,2})\/(\d{4})$/);
  if (!match) return null;
  const month = Number(match[1]);
  return month >= 1 && month <= 12
    ? `${match[2]}-${String(month).padStart(2, '0')}`
    : null;
};

export const toDisplayYearMonth = (yearMonth) => {
  const match = typeof yearMonth === 'string' && yearMonth.match(/^(\d{4})-(\d{2})$/);
  if (!match) return null;
  const month = Number(match[2]);
  return month >= 1 && month <= 12 ? `${month}/${match[1]}` : null;
};

export const toFirestoreType = (type) => {
  if (type === TRANSACTION_TYPES.INCOME) return FIRESTORE_TRANSACTION_TYPES.INCOME;
  if (type === TRANSACTION_TYPES.EXPENSE) return FIRESTORE_TRANSACTION_TYPES.EXPENSE;
  return null;
};

export const fromFirestoreType = (type) => {
  if (type === FIRESTORE_TRANSACTION_TYPES.INCOME) return TRANSACTION_TYPES.INCOME;
  if (type === FIRESTORE_TRANSACTION_TYPES.EXPENSE) return TRANSACTION_TYPES.EXPENSE;
  return null;
};

export const toFirestoreStatus = (status) => {
  if (status === TRANSACTION_STATUS.RECEIVED) return FIRESTORE_TRANSACTION_STATUS.RECEIVED;
  if (status === TRANSACTION_STATUS.PAID) return FIRESTORE_TRANSACTION_STATUS.PAID;
  if (status === TRANSACTION_STATUS.PENDING) return FIRESTORE_TRANSACTION_STATUS.PENDING;
  return null;
};

export const fromFirestoreStatus = (status) => {
  if (status === FIRESTORE_TRANSACTION_STATUS.RECEIVED) return TRANSACTION_STATUS.RECEIVED;
  if (status === FIRESTORE_TRANSACTION_STATUS.PAID) return TRANSACTION_STATUS.PAID;
  if (status === FIRESTORE_TRANSACTION_STATUS.PENDING) return TRANSACTION_STATUS.PENDING;
  return null;
};

const optionalId = (value) => (
  typeof value === 'string' && value.length > 0 && value.length <= 128 ? value : null
);

const optionalNameSnapshot = (value) => (
  typeof value === 'string' && value.length > 0 && value.length <= 120 ? value : null
);

const validSharingSnapshot = (value) => Boolean(value)
  && typeof value === 'object'
  && value.mode === 'snapshot'
  && typeof value.linkId === 'string' && value.linkId.length > 0 && value.linkId.length <= 128
  && typeof value.sourceAccountId === 'string' && value.sourceAccountId.length > 0 && value.sourceAccountId.length <= 128
  && ['transaction', 'recurringOccurrence'].includes(value.sourceKind)
  && typeof value.sourceItemId === 'string' && value.sourceItemId.length > 0 && value.sourceItemId.length <= 160
  && typeof value.sharedByUid === 'string' && value.sharedByUid.length > 0 && value.sharedByUid.length <= 128
  && value.sharedAt
  && value.version === 1;

export const buildFirestoreTransaction = (record, forcedType = null) => {
  const domainType = forcedType || record?.tipo;
  const type = toFirestoreType(domainType);
  const status = toFirestoreStatus(record?.status);
  const amountCents = getAmountCents(record);
  const date = getFinancialDate(record);
  if (!type || !status || !isValidStatusForType(domainType, record?.status) || amountCents === null || !date) return null;

  const payload = {
    type,
    status,
    amountCents,
    date,
    yearMonth: date.slice(0, 7),
    category: String(record?.categoria || '').slice(0, 80),
    name: String(record?.nome || '').slice(0, 160),
    schemaVersion: FIRESTORE_FINANCIAL_SCHEMA_VERSION,
  };
  const createdByUid = optionalId(record?.createdByUid);
  const attributedToUid = optionalId(record?.attributedToUid);
  const attributedToNameSnapshot = optionalNameSnapshot(record?.attributedToNameSnapshot);
  if (createdByUid) payload.createdByUid = createdByUid;
  if (attributedToUid) payload.attributedToUid = attributedToUid;
  if (attributedToNameSnapshot) payload.attributedToNameSnapshot = attributedToNameSnapshot;
  if (validSharingSnapshot(record?.sharing)) payload.sharing = { ...record.sharing };
  return payload;
};

export const fromFirestoreTransaction = (id, data = {}) => ({
  id,
  nome: data.name || '',
  valor: centsToLegacyValue(data.amountCents),
  data: data.date ? `${data.date}T00:00:00.000Z` : null,
  categoria: data.category || '',
  tipo: fromFirestoreType(data.type),
  status: fromFirestoreStatus(data.status),
  amountCents: data.amountCents,
  date: data.date || null,
  yearMonth: data.yearMonth || null,
  schemaVersion: data.schemaVersion || FINANCIAL_SCHEMA_VERSION,
  createdAt: data.createdAt || null,
  updatedAt: data.updatedAt || null,
  sourceType: data.type ?? null,
  sourceStatus: data.status ?? null,
  origin: data.origin ?? null,
  migrationVersion: data.migrationVersion ?? null,
  createdByUid: data.createdByUid ?? null,
  attributedToUid: data.attributedToUid ?? null,
  attributedToNameSnapshot: data.attributedToNameSnapshot ?? null,
  sharing: data.sharing ?? null,
});
